import React, { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, Button,
  Switch, TextField, IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  textPrimary, textSecondary, font, accent, border,
  primaryButtonSx,
} from "../../../layouts/schoolPortal/schoolPortalTheme";

const DAYS = [
  ["monday", "Monday"], ["tuesday", "Tuesday"], ["wednesday", "Wednesday"],
  ["thursday", "Thursday"], ["friday", "Friday"], ["saturday", "Saturday"], ["sunday", "Sunday"],
];

const timeInputSx = {
  width: 110,
  "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "13px" },
};

/**
 * The default-available window per weekday — students can book any of this
 * time unless the calendar carves out a "blocked" or "reserved" exception.
 * Edited here rather than on the calendar itself, since it's a template, not
 * a one-off change.
 */
export default function WorkingHoursDialog({ open, initialValue, onClose, onSave, saving }) {
  const [hours, setHours] = useState(initialValue);

  useEffect(() => {
    if (open) setHours(initialValue);
  }, [open, initialValue]);

  const setDay = (day, patch) => {
    setHours((prev) => ({ ...prev, [day]: { ...prev[day], ...patch } }));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "16px" } }}>
      <DialogTitle sx={{ fontFamily: font, fontWeight: 800, color: textPrimary, fontSize: "17px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        Working hours
        <IconButton size="small" onClick={onClose}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: textSecondary, mb: 2 }}>
          You're bookable across all of this by default. Use the calendar to close off or reserve individual times within it.
        </Typography>
        {DAYS.map(([key, label]) => {
          const day = hours?.[key] || { enabled: false, start: "09:00", end: "17:00" };
          return (
            <Box key={key} sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1, borderBottom: `1px solid ${border}` }}>
              <Switch
                size="small"
                checked={Boolean(day.enabled)}
                onChange={(e) => setDay(key, { enabled: e.target.checked })}
                sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: accent }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: accent } }}
              />
              <Typography sx={{ fontFamily: font, fontSize: "13px", fontWeight: 600, color: day.enabled ? textPrimary : textSecondary, width: 90 }}>
                {label}
              </Typography>
              {day.enabled ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto" }}>
                  <TextField size="small" type="time" value={day.start} onChange={(e) => setDay(key, { start: e.target.value })} sx={timeInputSx} />
                  <Typography sx={{ fontFamily: font, fontSize: "12px", color: textSecondary }}>–</Typography>
                  <TextField size="small" type="time" value={day.end} onChange={(e) => setDay(key, { end: e.target.value })} sx={timeInputSx} />
                </Box>
              ) : (
                <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: textSecondary, ml: "auto" }}>Unavailable</Typography>
              )}
            </Box>
          );
        })}
      </DialogContent>
      <DialogActions sx={{ p: 2.5, pt: 1.5 }}>
        <Button onClick={onClose} sx={{ color: textSecondary, textTransform: "none", fontFamily: font }}>Cancel</Button>
        <Button onClick={() => onSave(hours)} disabled={saving} sx={primaryButtonSx}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}
